import { ErrorMessage, Form, Formik, type FormikHelpers } from 'formik';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import {
  PdfFormFieldFillupService,
  TermsAndConditionsService,
  VendorTermsAndConditionsService,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import { useQueryClient } from 'react-query';
import ToastComponent from '../../../components/toast';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import { ActiveOptions } from '../../../constants/common';
import { Label, UploadWrapper } from '../../../components/input-text/input.styled';
import { handleOpenFile } from '../../../utils/global';
import Spinners from '../../../components/spinner';
import ImageViewerSingle from '../../../components/input-file/inputFileWithViewSingle';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import RichTextEditor from '../../../components/rich-text-editor';
import { TERMS_AND_CONDITIONS_VALIDATION } from '../../../utils/validations/validations';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import {
  useGetBrokeragePdfAvailableSources,
  useGetVendorOverrideTnC,
  useGetVendorTermAndConditionPageList,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import useGetTermAndConditionPageList from '../../../hooks/get/useGetTermAndConditionPageList';
import _, { isEmpty } from 'lodash';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import QuillRichTextEditor from '../../../components/rich-text-editor/quillRichTextEditor';

interface VendorTermAndConditionType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus?: any;
  status?: number;
  termAndConditionInitialData: { data: any; id: string };
  isLoading: boolean;
  tableInfo: TableParamsType;
  setTableInfo: any;
  dataRow?: string;
  id?: string;
}

const VendorTermAndConditionModel = ({
  isModalOpen,
  closeModal,
  modalStatus,
  status,
  setModalStatus,
  termAndConditionInitialData,
  isLoading,
  tableInfo,
  setTableInfo,
  dataRow,
  id,
}: VendorTermAndConditionType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: pageOption, isLoading: isPageOptionLoading } =
    useGetVendorTermAndConditionPageList(handleRequest);
  const [removedId, setRemovedId] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [targetOptions, setTargetOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [isTargetOptionsLoading, setIsTargetOptionsLoading] = useState<boolean>(false);
  // const [isDocumentRemoved, setIsDocumentRemoved] = useState<boolean>(false);
  const { data: brokerOption, isLoading: isBrokerOptionLoading } =
    useGetVendorOverrideTnC(handleRequest);
  const queryClient = useQueryClient();
  const { data: brokeragePdfAvailableSources, isLoading: isBrokeragePdfAvailableSourcesLoading } =
    useGetBrokeragePdfAvailableSources(handleRequest);

  const handleSubmit = (values: any, actions: FormikHelpers<any>): void => {
    setLoading(true);
    const documentList = termAndConditionInitialData.data?.document.map((item: any) => {
      return {
        documentFileName: item.file.name,
        documentUniqueName: item.file.uniqueName,
        isDeleted: removedId.includes(item.file.uniqueName),
      };
    });
    const indexValues: Record<string, string | number | boolean> = {};
    documentList?.map((item: Record<string, string>, index: number) => {
      Object.keys(item).forEach((k, i) => {
        indexValues[`documentDetails[${index}].${k}`] = item[k];
      });
      return null;
    });

    // Build mapping array from form values
    // Get newly selected mappings from form (object format)
    const newlySelectedMappings =
      brokeragePdfAvailableSources
        ?.filter((item) => values?.mapping?.[item?.value])
        ?.map((item) => ({
          source: values?.mapping?.[item?.value],
          target: item?.value,
        })) ?? [];

    // Combine existing mappings (if array) with newly selected ones
    let mapping: Array<{ source: string; target: string }> = [];
    if (Array.isArray(values?.mapping) && values?.mapping?.length > 0) {
      // Start with existing mappings from server
      const existingMappings = values.mapping.map((m: { source: string; target: string }) => ({
        source: m.source,
        target: m.target,
      }));
      // Merge: use newly selected values if available, otherwise keep existing
      const existingTargets = new Set(newlySelectedMappings.map((m) => m.target));
      const filteredExisting = existingMappings.filter(
        (m: { source: string; target: string }) => !existingTargets.has(m.target)
      );
      mapping = [...filteredExisting, ...newlySelectedMappings];
    } else {
      mapping = newlySelectedMappings;
    }

    // Convert mapping array to indexed format (mapping[0].source, mapping[0].target, etc.)
    const mappingIndexValues: Record<string, string> = {};
    mapping?.forEach((item, index) => {
      mappingIndexValues[`mapping[${index}].source`] = item.source;
      mappingIndexValues[`mapping[${index}].target`] = item.target;
    });

    const payload = {
      name: values.name,
      pageId: values.pageId,
      agreementStatement: values.agreementStatement,
      id: termAndConditionInitialData.data.id,
      documentContent: values.documentContent,
      document: !(values.document?.length as boolean) ? '' : values.document,
      isActive: values?.isActive === 'Active',
      companyId: values.companyId,
      ...mappingIndexValues,
      ...indexValues,
    };

    if (termAndConditionInitialData.data?.id as boolean) {
      handleRequest(
        VendorTermsAndConditionsService.putApiCustomerApiVendorTermsAndConditions({
          ...(payload as any),
          isDocumentRemoved: !(values.documentList?.length as boolean),
        })
      )
        .then((response) => {
          if (response != null) {
            closeModal();
            toast.success(<ToastComponent label='Success' message={'Terms & Conditions edited'} />);
            queryClient.invalidateQueries('getTermAndConditionList');
            setLoading(false);

            // actions.resetForm();
          } else {
            setLoading(false);
          }
        })
        .catch((error) => {
          console.log('error', error);
        })
        .finally(() => {
          // actions.resetForm();
        });
    } else {
      handleRequest(
        VendorTermsAndConditionsService.postApiCustomerApiVendorTermsAndConditions(payload as any)
      )
        .then((response: any) => {
          if (response != null) {
            closeModal();
            toast.success(
              <ToastComponent label='Success' message={'Vendor Terms & Conditions added'} />
            );
            void queryClient.invalidateQueries('getVendorTermAndConditionList');
            setLoading(false);
            // actions.resetForm();
          } else {
            setLoading(false);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {});
    }
  };

  const handleImageOpen = (id: string): void => {
    // handleRequest(
    //   TermsAndConditionsService.postApiCustomerApiAppTermsAndConditionsDownloadDocument(id)
    // )
    //   .then((data: any) => {
    //     handleOpenFile(data.name, data.content);
    //   })
    //   .catch((error: any) => {
    //     console.log('error', error);
    //   });
  };

  const fetchTargetOptionsFromPdf = async (file: File): Promise<void> => {
    setIsTargetOptionsLoading(true);
    try {
      const response = await handleRequest(
        PdfFormFieldFillupService.postApiCustomerApiBrokeragePdfInputFields({
          pdf: file,
        })
      );
      if (response) {
        const options = response.map((item) => ({
          label: item ?? '',
          value: item ?? '',
        }));
        setTargetOptions(options);
      }
    } catch (error) {
      console.log('Error fetching PDF input fields:', error);
      setTargetOptions([]);
    } finally {
      setIsTargetOptionsLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={termAndConditionInitialData.data}
      validationSchema={TERMS_AND_CONDITIONS_VALIDATION}
      onSubmit={handleSubmit}>
      {({
        touched,
        handleChange,
        errors,
        resetForm,
        setFieldValue,
        setFieldTouched,
        setFieldError,
        values,
      }) => {
        // Effect to fetch target options when document is uploaded or exists in documentList
        useEffect(() => {
          const uploadedFile = values?.document?.[0];
          if (uploadedFile && uploadedFile instanceof File && uploadedFile?.size <= 10485760) {
            fetchTargetOptionsFromPdf(uploadedFile);
          } else if (
            values?.fileName &&
            values?.documentList?.length > 0 &&
            values?.documentList[0]?.file?.uniqueName
          ) {
            // Fetch existing document and extract PDF fields
            setIsTargetOptionsLoading(true);
            handleRequest(
              TermsAndConditionsService.postApiCustomerApiAppTermsAndConditionsDownloadDocument(
                values.documentList[0].file.uniqueName
              )
            )
              .then((data: any) => {
                if (data?.content) {
                  // Convert base64 content to File
                  const byteCharacters = atob(data.content);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  const blob = new Blob([byteArray], { type: 'application/pdf' });
                  const file = new File([blob], data.name || 'document.pdf', {
                    type: 'application/pdf',
                  });
                  fetchTargetOptionsFromPdf(file);
                }
              })
              .catch((error: any) => {
                console.log('Error downloading document:', error);
                setIsTargetOptionsLoading(false);
              });
          }
        }, [values?.document, values?.documentList?.length]);

        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            title={`${modalStatus} Vendor Terms & Conditions`}
            handleSubmit={handleSubmit}
            loading={isLoading}
            valueEdited={_.isEqual(values, termAndConditionInitialData.data)}
            size='lg'
            id={id}
            children={
              <>
                {isLoading && isEmpty(termAndConditionInitialData.data.id) ? (
                  <Spinners />
                ) : (
                  <div className='flex flex-col justify-between'>
                    <div>
                      <Form>
                        <ModalBodyComponent>
                          <fieldset>
                            <div className='grid sm:grid-cols-12 gap-x-8'>
                              <div className='sm:col-span-5'>
                                <FormSelect
                                  name={'pageId'}
                                  id='pageId'
                                  label='Page'
                                  viewName='page'
                                  containerClassName='formGroup'
                                  setFieldValue={setFieldValue}
                                  onChange={(e) => {
                                    const pageName = pageOption?.find(
                                      (item: any) => item?.value === e
                                    )?.label;
                                    setFieldValue('page', pageName);
                                    handleChange(e);
                                  }}
                                  placeholder={'Select'}
                                  options={pageOption}
                                  isLoading={isPageOptionLoading}
                                  touched={touched}
                                  errors={errors}
                                  values={values}
                                  inputType={modalStatus}
                                  disabled={modalStatus === 'View'}
                                  required
                                />
                              </div>
                              {pageOption &&
                                pageOption.find((item: any) => item.value === values.pageId)
                                  ?.label === 'Wealthlane Brokerage' && (
                                  <div className='sm:col-span-5'>
                                    <FormSelect
                                      name={'companyId'}
                                      id='companyId'
                                      label='Broker'
                                      viewName='page'
                                      containerClassName='formGroup'
                                      setFieldValue={setFieldValue}
                                      onChange={handleChange}
                                      placeholder={'Select'}
                                      options={brokerOption}
                                      isLoading={isBrokerOptionLoading}
                                      touched={touched}
                                      errors={errors}
                                      values={values}
                                      inputType={modalStatus}
                                      disabled={modalStatus === 'View'}
                                      required
                                    />
                                  </div>
                                )}
                              <InputText
                                type='text'
                                label='Name'
                                className='col-span-7'
                                name='name'
                                id='name'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                inputType={modalStatus}
                                disabled={modalStatus === 'View'}
                                required
                              />
                            </div>
                            <div className='grid sm:grid-cols-12 gap-x-8'>
                              <InputText
                                type='text'
                                label='Agreement Statement '
                                className='col-span-9'
                                name='agreementStatement'
                                id='agreementStatement'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                inputType={modalStatus}
                                disabled={modalStatus === 'View'}
                                required
                              />
                              <div className='sm:col-span-3'>
                                <FormSelect
                                  name={'isActive'}
                                  id='isActive'
                                  label='Status'
                                  viewName='isActive'
                                  containerClassName='formGroup'
                                  setFieldValue={setFieldValue}
                                  onChange={handleChange}
                                  placeholder={'Select'}
                                  options={ActiveOptions}
                                  touched={touched}
                                  errors={errors}
                                  values={values}
                                  inputType={modalStatus}
                                  disabled={modalStatus === 'View'}
                                  required
                                />
                              </div>
                            </div>
                            <div className='grid grid-cols-12'>
                              <div className='col-span-12'>
                                <>
                                  <QuillRichTextEditor
                                    name='documentContent'
                                    setFieldValue={setFieldValue}
                                    value={values?.documentContent}
                                    error={errors?.documentContent}
                                    touched={touched?.documentContent}
                                    readOnly={modalStatus === 'View'}
                                  />
                                </>
                                <ErrorMessage
                                  name='documentContent'
                                  component='div'
                                  className='error-message'
                                />
                              </div>
                            </div>

                            <div className='gap-3 justify-between mt-8'>
                              <div>
                                <Label htmlFor={'document'} className={'pb-1'}>
                                  {modalStatus === 'View' ? '' : 'Upload'} Document
                                </Label>
                                {modalStatus === 'View' &&
                                (values.documentList[0]?.file.name === undefined ||
                                  values.documentList[0]?.file.name === null) ? (
                                  <UploadWrapper className='flex justify-center'>
                                    <Heading
                                      variant='h6'
                                      title='No document to show'
                                      color={COLORS.SpanishGray}
                                      className='flex justify-center'
                                      id='noDocumentToShow'
                                    />
                                  </UploadWrapper>
                                ) : (
                                  <ImageViewerSingle
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    modalStatus={modalStatus}
                                    values={values}
                                    errors={errors}
                                    errorValue={errors?.documentContent}
                                    touchedValue={touched?.document}
                                    touched={touched}
                                    name='document'
                                    listingName='documentList'
                                    loading={loading}
                                    removedId={removedId}
                                    handleImageOpen={handleImageOpen}
                                    setRemovedId={setRemovedId}
                                    uploadLimit={1}
                                    isSingleFile={true}
                                    setFieldError={setFieldError}
                                    fileType={'*PDF only(Not more than 10 MB)'}
                                  />
                                )}
                              </div>
                            </div>
                            {(values?.document?.length > 0 || values?.documentList?.length > 0) &&
                              values?.page === 'Wealthlane Brokerage' && (
                                <div className='mt-8'>
                                  {isTargetOptionsLoading ? (
                                    <Spinners />
                                  ) : (
                                    targetOptions &&
                                    targetOptions?.length > 0 &&
                                    brokeragePdfAvailableSources?.map((item, index) => (
                                      <div
                                        className='grid grid-cols-4 gap-x-4 items-start mb-4 gap-4'
                                        key={item?.value}>
                                        <div className='col-span-2 flex justify-start mt-4'>
                                          <Heading
                                            variant='h6'
                                            title={item?.label}
                                            color={COLORS.SpanishGray}
                                            className='flex justify-center'
                                            id={`source-${index}`}
                                          />
                                        </div>

                                        <div className='col-span-2'>
                                          <FormSelect
                                            name={`mapping.${item?.value}`}
                                            id={`mapping-${item?.value}`}
                                            viewName={`mapping.${item?.value}`}
                                            containerClassName='formGroup'
                                            setFieldValue={setFieldValue}
                                            onChange={(selectedOption: any) => {
                                              if (selectedOption !== null) {
                                                // Set the selected value
                                                if (Array.isArray(values?.mapping)) {
                                                  // Convert array to object and add new value
                                                  const mappingObj: Record<string, string> = {};
                                                  values.mapping.forEach(
                                                    (m: { source: string; target: string }) => {
                                                      if (m?.target && m?.source) {
                                                        mappingObj[m.target] = m.source;
                                                      }
                                                    }
                                                  );
                                                  mappingObj[item?.value] = selectedOption.value;
                                                  setFieldValue('mapping', mappingObj);
                                                } else {
                                                  setFieldValue(
                                                    `mapping.${item?.value}`,
                                                    selectedOption
                                                  );
                                                }
                                              } else {
                                                // Remove only this source when cleared
                                                if (Array.isArray(values?.mapping)) {
                                                  // Filter out the entry with matching target
                                                  const newMapping = values.mapping.filter(
                                                    (m: { source: string; target: string }) =>
                                                      m?.target !== item?.label
                                                  );
                                                  setFieldValue('mapping', newMapping);
                                                } else {
                                                  // Remove the key from mapping object
                                                  const newMapping = { ...values?.mapping };
                                                  delete newMapping[item?.value];
                                                  setFieldValue('mapping', newMapping);
                                                }
                                              }
                                              setFieldValue(
                                                `mapping.${item?.value}`,
                                                selectedOption
                                              );
                                            }}
                                            placeholder={'Select Source'}
                                            options={targetOptions}
                                            isLoading={isTargetOptionsLoading}
                                            touched={touched}
                                            errors={errors}
                                            values={values}
                                            arrValue={
                                              values?.mapping?.[item?.value] ??
                                              (Array.isArray(values?.mapping)
                                                ? values?.mapping?.find(
                                                    (m: { source: string; target: string }) =>
                                                      m?.target === item?.label
                                                  )?.source
                                                : undefined)
                                            }
                                            inputType={modalStatus}
                                            disabled={modalStatus === 'View'}
                                            isClearable={true}
                                          />
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                          </fieldset>
                        </ModalBodyComponent>
                        <ModalFooterComponent
                          id='tncModalFooter'
                          modalStatus={modalStatus}
                          onCloseModal={() => {
                            closeModal();
                          }}
                          valueChanged={_.isEqual(values, termAndConditionInitialData.data)}
                          onEditModal={() => setModalStatus('Edit')}
                          loading={loading}
                        />
                      </Form>
                    </div>
                  </div>
                )}
              </>
            }
          />
        );
      }}
    </Formik>
  );
};

export default VendorTermAndConditionModel;
